// The thread (middle) pane (docs/06-ux-rules.md "Thread"; veda:projects/
// unified-feedback-inbox/parts/p05-thread-pane.md; 11-chassis-plan.md §2
// PR 05). Owns the detail query, the resolve/unresolve actions, and
// item rendering. `InboxLayout.Thread` already supplies the pane's
// `<section aria-label>` landmark (FeedbackInbox/index.tsx) — this
// component renders directly into it, no second landmark nested inside.
import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useGetFeedbackInboxItemQuery } from 'api/feedbackInbox'
import type { FeedbackInboxDetail } from 'api/feedbackInbox.types'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useEffect, useRef } from 'react'
import { useStyletron } from 'styletron-react'
import { useViewerCan } from '..'
import ComposerBanner from '../InboxComposer/ComposerBanner'
import type { ThreadItem } from './buildThreadItems'
import { buildThreadItems } from './buildThreadItems'
import ChatMessageBubble from './ChatMessageBubble'
import EmailCard from './EmailCard'
import GuestMessage from './GuestMessage'
import NothingSelected from './NothingSelected'
import PastFeedbackIndicator from './PastFeedbackIndicator'
import ResolvedMarker from './ResolvedMarker'
import SystemNote from './SystemNote'
import ThreadHeader from './ThreadHeader'
import ThreadNote from './ThreadNote'
import type { InboxErrorKind, UseInboxActionsResult } from './useInboxActions'
import { actionFailure, useInboxActions } from './useInboxActions'

const t = buildTranslate('feedback_inbox.thread')
const tComposer = buildTranslate('feedback_inbox.composer')

// Header copy per failure kind (AI-1350) — network/5xx reuses the pre-
// existing `action_error` key.
const ACTION_ERROR_KEYS: Record<InboxErrorKind, string> = {
  network: 'action_error',
  validation: 'action_error_validation',
  permission: 'action_error_permission',
}

// Composer visibility rule (docs/06-ux-rules.md "Composer" / "Visibility";
// P6 D1) — lives here, not in the composer itself (PR 06), because the
// header's Mark-as-resolved/Unresolve toggle and the composer's presence
// are two faces of the same "is this item still actionable" question.
// Exported as a named pure fn for its own unit test, mirroring
// `deriveViewerCan`/`inboxRowStatus`'s "pure derivation, tested directly"
// pattern elsewhere in this feature.
//
// Deliberately open ∧ 👎 ONLY — no `viewerCan` term of its own. Kept this
// way so the pure/testable "is this item actionable" question stays
// separate from "can THIS viewer act on it". `ThreadPaneBody` (below) ANDs
// in `viewerCan.manage` itself before deciding whether to actually mount
// the composer — v70 hides the composer entirely for a view-only user
// (`#pane-respond.view-only .dmcomposer{display:none!important}`,
// VISUAL-SPEC.md §3; user decision 2026-09-20), replacing the earlier
// "viewers see the same composer, disabled" design (docs/06-ux-rules.md
// "Actions" is stale on this point). The view-only banner that used to live
// inside the composer for that case now renders directly from
// `ThreadPaneBody` in the composer's slot instead (see
// `showViewOnlyComposerBanner` below).
export function shouldShowComposer(detail: FeedbackInboxDetail): boolean {
  return detail.status === 'open' && detail.rating === 'down'
}

function renderItem(item: ThreadItem) {
  switch (item.type) {
    case 'guest':
      return (
        <GuestMessage
          name={item.name}
          initials={item.initials}
          text={item.text}
          at={item.at}
          quoted={item.quoted}
        />
      )
    case 'nocomment':
    case 'positive':
      return <ThreadNote text={item.text} at={item.at} />
    case 'automated_ack':
      return (
        <SystemNote variant="automated_ack" body={item.text} at={item.at} />
      )
    case 'escalation':
      return <SystemNote variant="escalation" body={item.text} at={item.at} />
    case 'reopened':
      return (
        <SystemNote
          variant="reopened"
          body={t('reopened_marker', { name: item.name })}
          at={item.at}
        />
      )
    case 'restricted':
      return <SystemNote variant="restricted" body={item.text} at={item.at} />
    case 'ai_chat':
      return <ChatMessageBubble side="right" text={item.text} at={item.at} />
    case 'ai_email':
      return (
        <EmailCard
          author={{ kind: 'ai', name: t('recovery_ai_name') }}
          to={item.to}
          subject={item.subject}
          body={item.text}
          at={item.at}
        />
      )
    case 'merchant_email':
      return (
        <EmailCard
          author={{ kind: 'merchant', name: item.authorName }}
          to={item.to}
          subject={item.subject}
          body={item.body}
          at={item.at}
        />
      )
    case 'resolved_marker':
      return <ResolvedMarker name={item.name} at={item.at} />
    default:
      return null
  }
}

export interface ThreadPaneProps {
  itemId: string | null
  // Viewing a past order from the right rail: shows the "Past feedback" strip.
  isLookback?: boolean
  onResolved: (id: string) => void
  // Failure-only signal (mirrors `onResolved`) — see `useInboxActions`'s
  // `onResolveFailed` doc.
  onResolveFailed?: (id: string) => void
  announce: (text: string) => void
  // PR 06's composer slot. `actions` is the SAME `useInboxActions` instance
  // this pane's own header uses (constructed once in `ThreadPaneBody`,
  // below, and threaded through here) — the composer's send and the
  // header's Mark-as-resolved/Unresolve share one mutation's pending/error/
  // reset state (CLAUDE.md mutation rule; docs/06-ux-rules.md "Actions" /
  // "Error reset"), rather than each holding a separate instance that could
  // disagree about whether a write is in flight.
  renderComposer?: (
    detail: FeedbackInboxDetail,
    actions: UseInboxActionsResult
  ) => React.ReactNode
  // Routes the header's Mark-as-resolved click through the container's
  // discard-confirm guard (docs/06-ux-rules.md "Composer" / "Discard-
  // confirm") — resolving flips `shouldShowComposer` false and unmounts the
  // composer, which would otherwise silently drop an unsent draft. Given
  // `discardGuard.confirmDiscard` directly: runs `proceed()` immediately
  // when there's nothing to lose, otherwise opens the SAME confirm dialog
  // the list's selection-change path uses. Omitted (e.g. in stories that
  // don't mount the container) resolves immediately, unguarded.
  beforeResolve?: (proceed: () => void) => void
}

const ThreadPane = ({
  itemId,
  isLookback = false,
  onResolved,
  onResolveFailed,
  announce,
  renderComposer,
  beforeResolve,
}: ThreadPaneProps) => {
  const [css] = useStyletron()
  const scrollRef = useRef<HTMLDivElement>(null)
  const {
    data: detail,
    isLoading,
    isError,
    refetch,
  } = useGetFeedbackInboxItemQuery(itemId ?? '', { skip: !itemId })

  // Opens scrolled to top; item change → scroll to top (docs/06-ux-rules.md
  // "Thread" / "Scroll behaviour", P5 D6). `scrollTo` isn't implemented by
  // jsdom (Element.prototype.scrollTo is undefined there) — guarded rather
  // than assuming it exists, so component tests don't need a global jsdom
  // polyfill just to render this pane.
  useEffect(() => {
    scrollRef.current?.scrollTo?.({ top: 0 })
  }, [itemId])

  if (!itemId) {
    return <NothingSelected />
  }

  if (isLoading) {
    return (
      <div
        aria-busy="true"
        aria-label={t('loading_aria')}
        className={css({
          height: '100%',
          padding: '24px',
          // Matches ThreadPaneBody's own grey scroll body (below) so the
          // skeleton doesn't flash white then grey once the real body
          // mounts.
          backgroundColor: palette.grey10,
        })}
      >
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={`thread-skeleton-${index}`}
            aria-hidden="true"
            className={css({
              height: '48px',
              maxWidth: '70%',
              marginBottom: '16px',
              borderRadius: '10px',
              // White + a grey20 border — visible against the grey10 body
              // above (grey10-on-grey10 would be invisible).
              backgroundColor: palette.white,
              border: `1px solid ${palette.grey20}`,
            })}
          />
        ))}
      </div>
    )
  }

  if (isError || !detail) {
    return (
      <div className={css({ padding: '32px 16px', textAlign: 'center' })}>
        <Text.BodyText3 tag="p" bold>
          {t('error_title')}
        </Text.BodyText3>
        <Button kind="secondary" onClick={refetch}>
          {t('retry')}
        </Button>
      </div>
    )
  }

  return (
    <ThreadPaneBody
      detail={detail}
      // `data` keeps the previous arg's result while a new arg loads (RTK
      // 1.9.5), so only label the strip once the detail is the look-back item.
      isLookback={isLookback && detail.id === itemId}
      onResolved={onResolved}
      onResolveFailed={onResolveFailed}
      announce={announce}
      renderComposer={renderComposer}
      beforeResolve={beforeResolve}
      scrollRef={scrollRef}
    />
  )
}

// Split out so `useInboxActions` (which needs a loaded, non-optional
// `detail`) is only ever called once the detail query has actually
// resolved — calling hooks conditionally in the parent would violate the
// rules of hooks.
function ThreadPaneBody({
  detail,
  isLookback,
  onResolved,
  onResolveFailed,
  announce,
  renderComposer,
  beforeResolve,
  scrollRef,
}: {
  detail: FeedbackInboxDetail
  isLookback: boolean
  onResolved: (id: string) => void
  onResolveFailed?: (id: string) => void
  announce: (text: string) => void
  renderComposer?: (
    detail: FeedbackInboxDetail,
    actions: UseInboxActionsResult
  ) => React.ReactNode
  beforeResolve?: (proceed: () => void) => void
  scrollRef: React.RefObject<HTMLDivElement>
}) {
  const [css] = useStyletron()
  const viewerCan = useViewerCan()
  const actions = useInboxActions({
    detail,
    onResolved,
    onResolveFailed,
    announce,
  })
  const items = buildThreadItems(detail)
  // AND in `viewerCan.manage` — a view-only user never mounts the real
  // composer (see `shouldShowComposer`'s own header comment).
  const rawShowComposer = shouldShowComposer(detail) && viewerCan.manage

  const failure = actionFailure(actions)

  // Keeps the composer mounted past what `rawShowComposer` says, for two
  // independent reasons — each needs its own ref because they expire
  // differently (AI-1350 review round 1).
  // context:
  //   1. Rollback-timing gap — a header failure (mark_resolved/unresolve)
  //      lands one render before its own undo()/refetch updates `detail`
  //      (useInboxActions' same-render lifecycle test). `justSettled...`
  //      covers exactly that render then expires; bridging any longer
  //      would latch the composer open on an item that's genuinely
  //      resolved (e.g. someone else resolved it concurrently).
  //   2. Draft protection (F3) — a failed Send deliberately keeps the
  //      draft mounted on a since-resolved item indefinitely, and must
  //      survive a LATER failed Unresolve too — keyed on "is there a
  //      draft to protect", not on which action most recently failed.
  const wasPendingRef = useRef(false)
  const justSettledWithFailure =
    wasPendingRef.current && !actions.isPending && failure !== null
  useEffect(() => {
    wasPendingRef.current = actions.isPending
  })

  const draftProtectedRef = useRef(false)
  useEffect(() => {
    // `failure` reads null the instant a NEW action starts too (it's
    // derived from THAT action's own not-yet-errored state) — gating the
    // clear on `!actions.isPending` as well keeps a later Unresolve's
    // pending window from wiping out a still-live Send failure's latch
    // before Unresolve has even had a chance to fail itself.
    if (!actions.isPending && failure === null) {
      draftProtectedRef.current = false
    } else if (failure?.action === 'send' && !rawShowComposer) {
      draftProtectedRef.current = true
    }
  })

  const showComposer =
    rawShowComposer ||
    actions.isPending ||
    draftProtectedRef.current ||
    (failure !== null &&
      failure.action !== 'unresolve' &&
      justSettledWithFailure)

  // The surface whose control fired the write shows its failure: a Send
  // failure shows in the composer (its own banner) whenever the composer is
  // visible, falling back to the header only if it somehow isn't (so a
  // failure is never silent). A header-control failure (mark_resolved /
  // unresolve) always shows under the header, composer visible or not.
  const headerErrorKind: InboxErrorKind | null =
    failure !== null && (failure.action !== 'send' || !showComposer)
      ? failure.kind
      : null

  // The composer's old view-only banner now renders from here instead —
  // exactly the case a view-only viewer would otherwise see a real composer
  // (open ∧ 👎), minus the `viewerCan.manage` term `rawShowComposer` ANDs
  // in above.
  const showViewOnlyComposerBanner =
    !showComposer && shouldShowComposer(detail) && !viewerCan.manage

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      })}
    >
      {isLookback && <PastFeedbackIndicator date={detail.date} />}
      <ThreadHeader
        orderNumber={detail.orderNumber}
        status={detail.status}
        rating={detail.rating}
        isPending={actions.isPending}
        onResolve={() =>
          beforeResolve
            ? beforeResolve(() => actions.resolve(undefined, 'mark_resolved'))
            : actions.resolve(undefined, 'mark_resolved')
        }
        onUnresolve={() => actions.unresolve()}
      />
      {headerErrorKind && (
        <div
          role="alert"
          className={css({
            padding: '10px 16px',
            backgroundColor: palette.cayenne05,
            color: palette.cayenne50,
            fontSize: '13px',
          })}
        >
          {t(ACTION_ERROR_KEYS[headerErrorKind])}
        </div>
      )}
      <div
        ref={scrollRef}
        className={css({
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
          padding: '20px 22px',
          // Grey scroll body, white bubbles/cards inside it (VISUAL-SPEC.md
          // §1 `.ib-thread`) — the header above and the composer below (PR
          // 06) stay white; only this scrollable middle strip is grey.
          backgroundColor: palette.grey10,
        })}
      >
        {items.map(item => (
          <div key={item.id}>{renderItem(item)}</div>
        ))}
      </div>
      {showComposer && renderComposer?.(detail, actions)}
      {showViewOnlyComposerBanner && (
        <div
          className={css({
            flexShrink: 0,
            backgroundColor: palette.white,
            borderTop: `1px solid ${palette.grey20}`,
            padding: '16px 22px',
          })}
        >
          <ComposerBanner tone="neutral" role="note">
            {tComposer('view_only_banner')}
          </ComposerBanner>
        </div>
      )}
    </div>
  )
}

export default ThreadPane
